import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { Menu, MenuItem, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ProgressButton } from "@qubit/autoparts";
import { Dispatch, MouseEvent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { GetPortResponse } from "~/redux/actions";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { NextEmptyBinResponse, PutAwayTaskSummaryDto } from "~/types/api";

export const BinExceptionsMenuItem = styled(MenuItem)(() => ({
  width: "250px"
}));

type Props = {
  areAllPortsOpen: boolean;
  areAllPortsReady: boolean;
  binExceptionsMenuAnchor: HTMLElement | null;
  closeBinExceptionsMenu: () => void;
  currentSelectedBin: NextEmptyBinResponse | null;
  handleClickBinExceptionsButton: (
    event: MouseEvent<HTMLButtonElement>
  ) => void;
  handleClickBinNotEmptyButton: () => void;
  handleClickFlagBin: () => void;
  handleClickPlaceInventoryHold: () => void;
  isBinExceptionsMenuOpen: boolean;
  shouldListenToGridEvents: boolean;
  isPlaceHoldDisabled: boolean;
  isBinNotEmptyButtonDisabled: boolean;
  isPutawayButtonDisabled: boolean;
  portStateByPortArray: {
    getPortResponse: GetPortResponse;
    timestamp: Date;
  }[];
  selectedRow: PutAwayTaskSummaryDto | undefined;
  setChangeSuggestedBinModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsPutawayModalOpen: Dispatch<SetStateAction<boolean>>;
};

export function ActionButtons(props: Props) {
  const {
    areAllPortsOpen,
    areAllPortsReady,
    binExceptionsMenuAnchor,
    closeBinExceptionsMenu,
    currentSelectedBin,
    handleClickBinExceptionsButton,
    handleClickBinNotEmptyButton,
    handleClickFlagBin,
    handleClickPlaceInventoryHold,
    isBinExceptionsMenuOpen,
    shouldListenToGridEvents,
    isPlaceHoldDisabled,
    isBinNotEmptyButtonDisabled,
    isPutawayButtonDisabled,
    portStateByPortArray,
    selectedRow,
    setChangeSuggestedBinModalOpen,
    setIsPutawayModalOpen
  } = props;
  const { t } = useTranslation();

  const { putaway_showExceptionsButton } = useAppSelector(selectServerConfig);

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      sx={{
        gap: {
          xs: 1,
          sm: 2,
          lg: 6
        }
      }}
    >
      {!putaway_showExceptionsButton && (
        <ProgressButton
          buttonSize="footer"
          emphasis="high"
          responsive
          variant="contained"
          fullWidth
          color="secondary"
          onClick={handleClickBinNotEmptyButton}
          disabled={isBinNotEmptyButtonDisabled}
        >
          {t("bin not empty")}
        </ProgressButton>
      )}

      <ProgressButton
        buttonSize="footer"
        emphasis="high"
        responsive
        variant="contained"
        color="primary"
        fullWidth
        startIcon={<SystemUpdateAltIcon style={{ fontSize: 22 }} />}
        onClick={(): Promise<void> | null => {
          setIsPutawayModalOpen((prev) => !prev);
          return null;
        }}
        disabled={isPutawayButtonDisabled}
      >
        {t("put away")}
      </ProgressButton>

      {putaway_showExceptionsButton && (
        <>
          <ProgressButton
            buttonSize="footer"
            emphasis="high"
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: selectedRow ? "warning.main" : ""
            }}
            endIcon={<KeyboardArrowUpIcon style={{ fontSize: 22 }} />}
            onClick={handleClickBinExceptionsButton}
            disabled={!selectedRow}
            fullWidth
            responsive
          >
            {t("exceptions")}
          </ProgressButton>
          <Menu
            anchorEl={binExceptionsMenuAnchor}
            open={isBinExceptionsMenuOpen}
            onClose={closeBinExceptionsMenu}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center"
            }}
            transformOrigin={{
              vertical: 200,
              horizontal: "center"
            }}
          >
            <BinExceptionsMenuItem
              disabled={!currentSelectedBin}
              onClick={() => {
                closeBinExceptionsMenu();
                setChangeSuggestedBinModalOpen(true);
              }}
            >
              {t("change config suggestion")}
            </BinExceptionsMenuItem>
            <BinExceptionsMenuItem
              disabled={
                (!areAllPortsReady && shouldListenToGridEvents) ||
                !areAllPortsOpen
              }
              onClick={handleClickBinNotEmptyButton}
            >
              {t("bin not empty")}
            </BinExceptionsMenuItem>
            <BinExceptionsMenuItem
              onClick={handleClickPlaceInventoryHold}
              disabled={isPlaceHoldDisabled}
            >
              {t("place inventory hold")}
            </BinExceptionsMenuItem>
            <BinExceptionsMenuItem
              onClick={handleClickFlagBin}
              disabled={
                !portStateByPortArray.length ||
                portStateByPortArray.every(
                  (port) => !port.getPortResponse.isReady
                )
              }
            >
              {t("flag bin")}
            </BinExceptionsMenuItem>
            <BinExceptionsMenuItem onClick={closeBinExceptionsMenu}>
              {t("product too large")}
            </BinExceptionsMenuItem>
          </Menu>
        </>
      )}
    </Stack>
  );
}

export default ActionButtons;
